const get = require('lodash/get')
const DiscountPgAdapter = require('./pg-adapter')
const { isUuid } = require('../freshsales/tools')
const { unitDiscount } = require('./unit-discount')

// What a planner may edit. status, approved_by and rejected_by are deliberately absent:
// a discount only changes state through changeStatus, which is approver-only.
const UPDATABLE_FIELDS = {
  description: 'description',
  percentage: 'discount_percentage',
  amount: 'discount_amount',
  currency: 'currency',
  discountLevel: 'application_level',
  startDate: 'start_date',
  endDate: 'end_date'
}

const assertAllResolved = (level, ids, lookupIds, resolvedIds) => {
  const resolved = new Set(resolvedIds)
  const notFound = ids.filter((id, index) => !resolved.has(lookupIds[index]))
  if (notFound.length > 0) {
    const error = new Error(`Discount ${level} items not found`)
    error.errors = notFound
    error.status = 404
    throw error
  }
  return resolvedIds
}

// Tie-break only; the cheapest price for the client always wins first.
const LEVEL_PRECEDENCE = ['order', 'product', 'location', 'market', 'organisation']

// Location, market and organisation items were already matched to the location by
// the query, so they cover every product; product and order items name their target.
const itemCovers = (level, item, productId, orderId) => {
  if (level === 'product') return item.itemId === productId
  if (level === 'order') return item.itemId === orderId
  return true
}

const discountedPrice = (discount, sellPrice) =>
  Math.max(sellPrice - unitDiscount(sellPrice, discount.discountPercentage, discount.discountAmount), 0)

// A discount may take up to and including this share off the sku's current sell price
const MAX_DISCOUNT_RATIO = 0.5

const withinDiscountLimit = (discount, currentSellPrice) => {
  // Without a current price there is nothing to measure the discount against
  if (!currentSellPrice) return false
  // Compared without dividing, so an exact 50% isn't refused by float rounding
  if (discount.discountPercentage !== null) {
    return Number(discount.discountPercentage) <= MAX_DISCOUNT_RATIO * 100
  }
  return Number(discount.discountAmount) <= currentSellPrice * MAX_DISCOUNT_RATIO
}

// Set by order.decorateOrderWithResolvedPrices, even when the price is null.
const hasResolvedPrices = order => Object.values(get(order, 'products', {}))
  .every(product => product && 'unitSellPrice' in product)

const pickDiscounts = (discounts, productIds, sellPrices, orderId, currentSellPrices) => {
  const picked = {}
  productIds.forEach(productId => {
    const sellPrice = sellPrices.get(productId)
    // Without a price there is nothing to compare a fixed amount against.
    if (sellPrice === undefined || sellPrice === null) return

    let best = null
    discounts.forEach(discount => {
      const level = discount.application_level
      const item = discount.items.find(candidate => itemCovers(level, candidate, productId, orderId))
      if (!item) return
      // Over the limit discounts are skipped, so the best one within it can still win
      if (!withinDiscountLimit(discount, currentSellPrices.get(productId))) return

      const price = discountedPrice(discount, sellPrice)
      const rank = LEVEL_PRECEDENCE.indexOf(level)
      if (!best || price < best.price || (price === best.price && rank < best.rank)) {
        best = { price, rank, discount, item }
      }
    })

    if (best) {
      const { discountPercentage, discountAmount } = best.discount
      picked[productId] = {
        discountItemId: best.item.id,
        discountPercentage,
        // A percentage discount is turned into its unit amount at this sell price
        discountAmount: discountPercentage !== null
          ? unitDiscount(sellPrice, discountPercentage)
          : discountAmount
      }
    }
  })
  return picked
}

class DiscountApi {
  constructor (state, pgConnection, agaveAdapter, logger, orderApi, priceAdapter) {
    this.agaveAdapter = agaveAdapter
    this.pgAdapter = new DiscountPgAdapter(pgConnection)
    this.logger = logger
    this.orderApi = orderApi
    this.priceAdapter = priceAdapter
  }

  getResolver (discountLevel) {
    return {
      location: async (ids) => {
        const resolvedLocationIds = await this.pgAdapter.getLocations(ids)
        const itemIds = assertAllResolved('location', ids, ids, resolvedLocationIds)
        return itemIds.map(id => ({ location_id: id }))
      },
      product: async (ids) => {
        const skuIds = ids.map(id => id.replace('product:', ''))
        const resolvedSkuIds = await this.pgAdapter.getProductSkus(skuIds)
        const itemIds = assertAllResolved('product', ids, skuIds, resolvedSkuIds)
        return itemIds.map(id => ({ product_sku_id: id }))
      },
      market: async (ids) => {
        const resolvedMarketIds = await this.pgAdapter.getMarkets(ids)
        const itemIds = assertAllResolved('market', ids, ids, resolvedMarketIds)
        return itemIds.map(id => ({ market_id: id }))
      },
      order: async (ids) => {
        return ids.map(id => ({ order_id: id }))
      },
      organisation: async (ids) => {
        // data_organisation.id is a uuid column, so a malformed id makes postgres
        // raise 22P02 - a 500 - instead of telling the caller which id was wrong
        const malformed = ids.filter(id => !isUuid(id))
        if (malformed.length > 0) {
          const error = new Error('Discount organisation items must be uuids')
          error.errors = malformed
          error.status = 400
          throw error
        }
        const resolvedOrganisationIds = await this.pgAdapter.getOrganisations(ids)
        const itemIds = assertAllResolved('organisation', ids, ids, resolvedOrganisationIds)
        return itemIds.map(id => ({ organisation_id: id }))
      }
    }[discountLevel]
  }

  // Resolves the items for a level, then optionally narrows a product discount to one
  // location, so each item carries both the sku and the location it applies at.
  async resolveItems (discountLevel, items, locationId) {
    const resolverFunction = this.getResolver(discountLevel)
    if (!resolverFunction) {
      this.logger.error({ code: 'Discount level resolver not found', discountLevel })
      const error = new Error('Discount level not supported')
      error.status = 400
      throw error
    }

    const resolvedItems = await resolverFunction(items.map(item => item.id))
    if (!locationId) {
      return resolvedItems
    }
    if (discountLevel !== 'product') {
      const error = new Error('Only a product level discount can be scoped to a location')
      error.status = 400
      throw error
    }

    const [scope] = await this.getResolver('location')([locationId])
    return resolvedItems.map(item => ({ ...item, location_id: scope.location_id }))
  }

  /**
   * Picks at most one discount item per product in the order: the one leaving the
   * lowest unit price. Ties go to the more specific level (see LEVEL_PRECEDENCE).
   * Discounts taking more than MAX_DISCOUNT_RATIO off the current sell price are never picked.
   * @param {string} locationId location fsid
   * @param {string} orderId
   * @param {Object} [options]
   * @param {Object} [options.orderDoc] skips fetching the order when already loaded
   * @returns {Promise<Object<string, {discountItemId: string, discountPercentage: ?number, discountAmount: ?number}>>}
   *   keyed by product id; products with no applicable discount are left out
   */
  async resolveOrderDiscount (locationId, orderId, { orderDoc } = {}) {
    const fetchedOrder = orderDoc || await this.orderApi.get({ orderId })
    // Discounts are priced off unitSellPrice, so an undecorated order gets its prices first.
    const order = hasResolvedPrices(fetchedOrder)
      ? fetchedOrder
      : await this.orderApi.decorateOrderWithResolvedPrices(fetchedOrder)
    const productIds = Object.keys(order.products || {})
    if (productIds.length === 0) {
      return order
    }

    // Current prices (not the order's) decide whether a discount stays within MAX_DISCOUNT_RATIO
    const [discounts, { prices: currentPrices }] = await Promise.all([
      this.pgAdapter.getApplicableDiscounts(locationId, order.orderId),
      this.priceAdapter.getPriceList({ skus: productIds })
    ])
    const sellPrices = new Map(productIds.map(productId => [productId, order.products[productId].unitSellPrice]))
    const currentSellPrices = new Map(currentPrices.map(price => [price.sku, price.sellprice]))
    const picked = pickDiscounts(discounts, productIds, sellPrices, order.orderId, currentSellPrices)

    const products = productIds.reduce((acc, productId) => {
      const discount = picked[productId] || {}
      acc[productId] = {
        ...order.products[productId],
        discountItemId: discount.discountItemId || null,
        discountPercentage: get(discount, 'discountPercentage', null),
        discountAmount: get(discount, 'discountAmount', null)
      }
      return acc
    }, {})

    return {
      ...order,
      products
    }
  }

  /**
   * Rejects a discount that takes more than MAX_DISCOUNT_RATIO off a current sell price.
   * A percentage is checked at every level. A fixed amount can only be measured against
   * named products, so other levels are left to resolveOrderDiscount, which skips them per sku.
   */
  async assertWithinDiscountLimit ({ percentage, amount, discountLevel, productIds: rawProductIds = [] }) {
    // Items may be sent with or without the prefix the price list keys its skus by
    const productIds = rawProductIds.map(id => `product:${`${id}`.replace('product:', '')}`)
    const limitPercent = MAX_DISCOUNT_RATIO * 100
    if (percentage != null && Number(percentage) > limitPercent) {
      const error = new Error(`A discount can be at most ${limitPercent}% of the sell price`)
      error.status = 400
      throw error
    }

    if (amount == null || discountLevel !== 'product' || productIds.length === 0) {
      return
    }

    const { prices } = await this.priceAdapter.getPriceList({ skus: productIds })
    const sellPrices = new Map(prices.map(price => [price.sku, price.sellprice]))
    const fixedDiscount = { discountPercentage: null, discountAmount: Number(amount) }
    const rejected = productIds.filter(productId => !withinDiscountLimit(fixedDiscount, sellPrices.get(productId)))
    if (rejected.length > 0) {
      const error = new Error(`A discount can be at most ${limitPercent}% of each product's current sell price`)
      error.errors = rejected.map(productId => ({
        productId,
        // null when the product has no current price to measure against
        sellPrice: sellPrices.get(productId) == null ? null : sellPrices.get(productId)
      }))
      error.status = 400
      throw error
    }
  }

  async create ({entry, createdBy}) {
    const { discountLevel } = entry
    await this.assertWithinDiscountLimit({
      percentage: entry.percentage,
      amount: entry.amount,
      discountLevel,
      productIds: (entry.items || []).map(item => item.id)
    })
    const resolvedItems = await this.resolveItems(discountLevel, entry.items, entry.locationId)
    const discountRow = {
      description: entry.description,
      discount_percentage: entry.percentage,
      discount_amount: entry.amount,
      currency: entry.currency,
      application_level: discountLevel,
      status: 'pending',
      is_active: false,
      start_date: entry.startDate,
      end_date: entry.endDate,
      created_by: createdBy,
      updated_by: createdBy,
      approved_by: null,
      rejected_by: null
    }
    const response = await this.pgAdapter.create(discountRow, resolvedItems)
    return response
  }

  async getById (id) {
    const row = await this.pgAdapter.get(id)
    if (!row) {
      const error = new Error('Discount not found')
      error.status = 404
      throw error
    }
    return row
  }

  async getList (page, limit, sort) {
    const response = await this.pgAdapter.getList(page, limit, sort)
    return {
      results: response.data,
      count: response.paging.total
    }
  }

  // Both update and delete are only allowed while a discount is still pending.
  async getPendingById (id) {
    const discount = await this.getById(id)
    if (discount.status !== 'pending') {
      const error = new Error(`Discount has already been marked as '${discount.status}'`)
      error.status = 409
      throw error
    }
    return discount
  }

  async update (id, entry, updatedBy) {
    const discount = await this.getPendingById(id)

    const discountRow = { updated_by: updatedBy }
    Object.keys(UPDATABLE_FIELDS).forEach(field => {
      if (entry[field] !== undefined) {
        discountRow[UPDATABLE_FIELDS[field]] = entry[field]
      }
    })

    const discountLevel = entry.discountLevel || discount.application_level
    // Check what the discount will be after this edit: sent values win over stored ones
    await this.assertWithinDiscountLimit({
      percentage: entry.percentage !== undefined ? entry.percentage : discount.discount_percentage,
      amount: entry.amount !== undefined ? entry.amount : discount.discount_amount,
      discountLevel,
      productIds: entry.items
        ? entry.items.map(item => item.id)
        : (discount.items || []).map(item => `product:${item.item_id}`)
    })
    if (entry.items) {
      const resolvedItems = await this.resolveItems(discountLevel, entry.items, entry.locationId)
      await this.pgAdapter.replaceItems(id, resolvedItems)
    } else if (entry.discountLevel && entry.discountLevel !== discount.application_level) {
      // The existing items belong to the old level, so they would no longer resolve.
      const error = new Error('Changing the discount level requires new items')
      error.status = 400
      throw error
    }

    await this.pgAdapter.update(id, discountRow, 'pending')
    return this.getById(id)
  }

  async delete (id) {
    await this.getPendingById(id)
    return this.pgAdapter.delete(id)
  }

  // Activation is the one change an approved discount still accepts, and only an
  // approver may make it. Everything else about it is frozen by then.
  async changeActivation (id, isActive, updatedBy) {
    const discount = await this.getById(id)
    if (discount.status !== 'approved') {
      const error = new Error(`Only an approved discount can be activated, this one is '${discount.status}'`)
      error.status = 409
      throw error
    }

    await this.pgAdapter.update(id, { is_active: isActive, updated_by: updatedBy }, 'approved')
    return this.getById(id)
  }

  async changeStatus (id, status, updatedBy) {
    const discount = await this.getById(id)
    if (discount.status !== 'pending') {
      const error = new Error(`Discount has already been marked as '${discount.status}'`)
      error.status = 409
      throw error
    }
    const updateValue = {
      updated_by: updatedBy,
      ...(status === 'approved'
        ? { approved_by: updatedBy, status, is_active: true }
        : status === 'rejected'
          ? { rejected_by: updatedBy, status }
          : {}
      )
    }
    const update = await this.pgAdapter.update(id, updateValue, 'pending')
    return update
  }
}

module.exports = DiscountApi
